import { motion } from 'framer-motion';
import { useState } from 'react';
import { useGeolocation } from '../../../hooks/useGeolocation';
import { useNearbyAdoptions } from '../../../hooks/useNearbyAdoptions';

export default function ShelterPartners() {
  const [showMap, setShowMap] = useState(false);
  const { location } = useGeolocation();
  const { shelters: liveShelters, isLoading } = useNearbyAdoptions(
    location?.latitude || null,
    location?.longitude || null
  );

  // Use live data if available
  const shelters = liveShelters || [];
  const isLiveData = liveShelters && liveShelters.length > 0;

  const getDirectionsUrl = (shelter: typeof shelters[0]) => {
    const address = `${shelter.address}, ${shelter.city}, ${shelter.state} ${shelter.postcode}`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  return (
    <section className="py-12 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <h2 className="font-heading font-bold text-2xl sm:text-4xl lg:text-5xl text-gray-900">
              {isLiveData ? 'Nearby' : 'Our'} <span className="text-accent">Shelter</span> Partners
            </h2>
            {isLiveData && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-body text-green-700 text-xs font-medium">Live Data</span>
              </div>
            )}
          </div>
          <p className="font-body text-gray-500 text-sm sm:text-base max-w-xl mx-auto mb-6">
            {isLiveData 
              ? `${shelters.length} trusted shelters near ${location?.city || 'you'} with cats ready for adoption.`
              : 'We work with trusted local shelters to connect you with cats who need loving homes.'
            }
          </p>

          {/* Map Toggle */}
          {isLiveData && location && (
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowMap(false)}
                className={`px-5 py-2.5 font-heading font-bold text-sm rounded-full transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                  !showMap
                    ? 'bg-accent text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-list-check text-base"></i>
                List View
              </button>
              <button
                onClick={() => setShowMap(true)}
                className={`px-5 py-2.5 font-heading font-bold text-sm rounded-full transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                  showMap
                    ? 'bg-accent text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-map-pin-line text-base"></i>
                Map View
              </button>
            </div>
          )}
        </div>

        {/* Map View */}
        {showMap && isLiveData && location && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8 rounded-2xl overflow-hidden border border-gray-200 shadow-lg"
          >
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d50000!2d${location.longitude}!3d${location.latitude}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1234567890`}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Nearby animal shelters map"
            ></iframe>
          </motion.div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-100 rounded-2xl animate-pulse">
                <div className="w-full h-40 sm:h-48 bg-gray-200"></div>
                <div className="p-4 sm:p-5 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Shelters Grid */}
        {!isLoading && shelters.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {shelters.map((shelter, index) => (
              <motion.div
                key={shelter.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group"
              >
                <div className="relative w-full h-40 sm:h-48 overflow-hidden">
                  <img
                    src={`https://public.readdy.ai/ai/img_res/86a75c54a12e5763781fa16850c4296a.jpg${shelter.id}&orientation=landscape`}
                    alt={`${shelter.name} - local cat shelter in ${shelter.city}`}
                    title={shelter.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://readdy.ai/api/search-image?query=friendly%20animal%20shelter%20building%20with%20welcoming%20entrance%20and%20clean%20modern%20design&width=600&height=400&seq=fallback&orientation=landscape';
                    }}
                  />
                  <div className="absolute top-3 right-3 bg-accent text-white px-3 py-1 rounded-full text-xs font-heading font-bold">
                    {shelter.catsAvailable} cats
                  </div>
                  {isLiveData && shelter.distance !== undefined && (
                    <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-gray-900 px-3 py-1 rounded-full text-xs font-heading font-bold flex items-center gap-1">
                      <i className="ri-map-pin-line text-accent"></i>
                      {shelter.distance.toFixed(1)} mi
                    </div>
                  )}
                </div>
                <div className="p-4 sm:p-5">
                  <h3 className="font-heading font-bold text-base sm:text-lg text-gray-900 mb-3">{shelter.name}</h3>
                  
                  <div className="space-y-2 mb-4">
                    <p className="font-body text-xs sm:text-sm text-gray-600 flex items-start gap-2">
                      <i className="ri-map-pin-line text-accent text-sm w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                      <span>{shelter.address}, {shelter.city}, {shelter.state} {shelter.postcode}</span>
                    </p>
                    <p className="font-body text-xs sm:text-sm text-gray-600 flex items-center gap-2">
                      <i className="ri-phone-line text-accent text-sm w-4 h-4 flex items-center justify-center"></i>
                      <a href={`tel:${shelter.phone}`} className="hover:text-accent transition-colors">
                        {shelter.phone}
                      </a>
                    </p>
                    {shelter.email && (
                      <p className="font-body text-xs sm:text-sm text-gray-600 flex items-center gap-2">
                        <i className="ri-mail-line text-accent text-sm w-4 h-4 flex items-center justify-center"></i>
                        <a href={`mailto:${shelter.email}`} className="hover:text-accent transition-colors truncate">
                          {shelter.email}
                        </a>
                      </p>
                    )}
                    {shelter.hours && (
                      <p className="font-body text-xs sm:text-sm text-gray-600 flex items-start gap-2">
                        <i className="ri-time-line text-accent text-sm w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                        <span>{shelter.hours}</span>
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <a
                      href={getDirectionsUrl(shelter)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-4 py-2.5 bg-accent hover:bg-accent/90 text-white font-heading font-bold text-xs sm:text-sm rounded-lg transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-navigation-line text-base"></i>
                      Directions
                    </a>
                    <a
                      href={`tel:${shelter.phone}`}
                      className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-heading font-bold text-xs sm:text-sm rounded-lg transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line text-base"></i>
                      Call
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && shelters.length === 0 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-home-heart-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">
              No Shelters Found
            </h3>
            <p className="font-body text-gray-600 text-sm">
              Try expanding your search radius or check back later.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}